import { Base } from '@studiometa/js-toolkit';
import { addClass, removeClass, nextFrame } from '@studiometa/js-toolkit/utils';
import Tabs from './Tabs.js';

/**
 * @typedef {{
 *   $refs: {
 *     iconSearch: HTMLButtonElement;
 *     formSearch: HTMLFormElement;
 *     inputSearch: HTMLInputElement;
 *     loader: HTMLElement;
 *     resultsPanel: HTMLElement;
 *     defaultPanel: HTMLElement;
 *     noResults: HTMLElement;
 *     popular: HTMLButtonElement[];
 *   };
 *   $options: {
 *     ajaxUrl: string;
 *   };
 * }} SearchProps
 */

/**
 * Live search component mounted on the nav element.
 *
 * Orchestrates open/close, debounced fetch and panel visibility. Results are
 * rendered server-side (ajax/search-results.twig) and injected into the
 * results panel; the default panel and its "no results" fallback are pure Twig.
 * @augments {Base<SearchProps>}
 */
export default class Search extends Base {
  /**
   * @type {import('@studiometa/js-toolkit').BaseConfig}
   */
  static config = {
    name: 'Search',
    refs: [
      'iconSearch',
      'formSearch',
      'inputSearch',
      'loader',
      'resultsPanel',
      'defaultPanel',
      'noResults',
      'popular[]',
    ],
    options: {
      ajaxUrl: { type: String, default: '/wp-admin/admin-ajax.php' },
    },
    components: { Tabs },
  };

  isOpen = false;

  lastKeyword = '';

  /** @type {ReturnType<typeof setTimeout>|null} */
  closeTimeout = null;

  /** @type {ReturnType<typeof setTimeout>|null} */
  fetchTimer = null;

  /** @type {AbortController|null} */
  abortController = null;

  /** @returns {HTMLElement|null} */
  get backdrop() {
    return document.querySelector('#search-backdrop');
  }

  /** @returns {HTMLElement|null} */
  get burger() {
    return document.querySelector('[data-ref="burgerBtn"]');
  }

  /**
   * Wire the debounced fetch once the component is in the DOM.
   */
  mounted() {
    this.debouncedFetch = (/** @type {string} */ keyword) => {
      clearTimeout(this.fetchTimer);
      this.fetchTimer = setTimeout(() => this.fetch(keyword), 400);
    };
  }

  /**
   * Tear down timers and in-flight requests.
   */
  destroyed() {
    clearTimeout(this.fetchTimer);
    if (this.closeTimeout) clearTimeout(this.closeTimeout);
    if (this.abortController) this.abortController.abort();
  }

  // ─── Panels ─────────────────────────────────────────────────────────────────

  /**
   * Show the default panel (popular searches, shortcuts, recommendations),
   * optionally prefixed with a server-rendered "no results" message.
   * @param {string} [message] HTML snippet to display above the base elements.
   */
  showDefaultPanel(message = '') {
    this.$refs.noResults.innerHTML = message;
    removeClass(this.$refs.defaultPanel, ['opacity-0', 'pointer-events-none']);
  }

  /**
   * Hide the default panel.
   */
  hideDefaultPanel() {
    addClass(this.$refs.defaultPanel, ['opacity-0', 'pointer-events-none']);
  }

  /**
   * Inject server-rendered results and mount the nested Tabs components.
   * @param {string} html
   */
  showResultsPanel(html) {
    this.$refs.resultsPanel.innerHTML = html;
    removeClass(this.$refs.resultsPanel, ['opacity-0', 'pointer-events-none']);
    this.$update();
  }

  /**
   * Hide the results panel.
   */
  hideResultsPanel() {
    addClass(this.$refs.resultsPanel, ['opacity-0', 'pointer-events-none']);
  }

  // ─── Open / Close ──────────────────────────────────────────────────────────

  /**
   * Open the search form.
   */
  async onIconSearchClick() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    const { formSearch, inputSearch } = this.$refs;

    removeClass(formSearch, 'hidden');
    addClass(formSearch, 'flex');

    await nextFrame();

    formSearch.style.transform = 'translateY(0)';
    inputSearch.focus();

    if (this.backdrop) removeClass(this.backdrop, ['opacity-0', 'pointer-events-none']);
    if (this.burger) addClass(this.burger, '!hidden');

    if (!inputSearch.value.trim()) this.showDefaultPanel();

    this.isOpen = true;
  }

  /**
   * Close the search form.
   */
  closeSearch() {
    if (this.closeTimeout) clearTimeout(this.closeTimeout);

    const { formSearch } = this.$refs;
    formSearch.style.transform = 'translateY(-100%)';

    this.closeTimeout = setTimeout(() => {
      removeClass(formSearch, 'flex');
      addClass(formSearch, 'hidden');
      formSearch.style.transform = '';
      this.closeTimeout = null;
    }, 510);

    if (this.burger) removeClass(this.burger, '!hidden');
    if (this.backdrop) addClass(this.backdrop, ['opacity-0', 'pointer-events-none']);

    this.hideResultsPanel();
    this.hideDefaultPanel();
    this.lastKeyword = '';
    this.isOpen = false;
  }

  // ─── Input & keyboard ─────────────────────────────────────────────────────

  /**
   *
   */
  onInputSearchInput() {
    this.triggerSearch();
  }

  /**
   *
   */
  onInputSearchFocus() {
    if (!this.$refs.inputSearch.value.trim()) {
      this.hideResultsPanel();
      this.showDefaultPanel();
      this.lastKeyword = '';
      return;
    }

    if (!this.$refs.resultsPanel.classList.contains('opacity-0')) return;

    this.lastKeyword = '';
    this.triggerSearch();
  }

  /**
   * Close the panel on Escape, regardless of which element has focus.
   * @param {{ event: KeyboardEvent }} props
   */
  keyed({ event }) {
    if (event.key === 'Escape' && this.isOpen) this.closeSearch();
  }

  // ─── Popular terms ──────────────────────────────────────────────────────────

  /**
   * Fill the input with a popular term and run the search.
   * @param {{ index: number }} props
   */
  onPopularClick({ index }) {
    const term = this.$refs.popular[index]?.dataset.searchPopular ?? '';
    if (!term) return;
    this.$refs.inputSearch.value = term;
    this.hideDefaultPanel();
    this.lastKeyword = '';
    this.triggerSearch();
  }

  // ─── Internal ──────────────────────────────────────────────────────────────

  /**
   * Read the input value and trigger a debounced fetch.
   */
  triggerSearch() {
    const q = this.$refs.inputSearch.value.trim().replaceAll(/\s+/g, ' ');

    if (!q) {
      clearTimeout(this.fetchTimer);
      if (this.abortController) this.abortController.abort();
      this.hideResultsPanel();
      this.showDefaultPanel();
      this.lastKeyword = '';
      addClass(this.$refs.loader, ['opacity-0', 'pointer-events-none']);
      return;
    }

    if (q !== this.lastKeyword) {
      this.lastKeyword = q;
      removeClass(this.$refs.loader, ['opacity-0', 'pointer-events-none']);
      this.debouncedFetch(q);
    }
  }

  /**
   * Fetch results from the server.
   * @param {string} keyword
   */
  fetch(keyword) {
    const { loader } = this.$refs;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    removeClass(loader, ['opacity-0', 'pointer-events-none']);

    window
      .fetch(this.$options.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `action=live_search&s=${encodeURIComponent(keyword)}`,
        signal: this.abortController.signal,
      })
      .then((r) => r.json())
      .then((data) => {
        addClass(loader, ['opacity-0', 'pointer-events-none']);

        if (data.keyword !== this.lastKeyword) return;

        // No results: show the "no results" message above the default panel's
        // base elements (shortcuts, popular searches, recommendations…).
        if (!data.hasResults) {
          this.hideResultsPanel();
          this.showDefaultPanel(data.template);
          return;
        }

        this.hideDefaultPanel();
        this.showResultsPanel(data.template);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        addClass(loader, ['opacity-0', 'pointer-events-none']);
        this.hideResultsPanel();
        if (this.lastKeyword === keyword) this.lastKeyword = '';
      });
  }
}
